import React, { useState } from 'react';
import axios from 'axios';

const isDev = process.env.NODE_ENV === 'development';
const BACKEND_URL = isDev ? '' : process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL
  ? `${BACKEND_URL.replace(/\/$/, '')}/api`
  : '/api';

const Contact = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null); // { type: 'success'|'error', text: string }
  const [loading, setLoading] = useState(false);
  // Honeypot field to reduce spam
  const [website, setWebsite] = useState('');

  const formatApiError = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const parts = detail
        .map((d) => {
          if (typeof d === 'string') return d;
          if (d && typeof d === 'object' && typeof d.msg === 'string') return d.msg;
          try {
            return JSON.stringify(d);
          } catch {
            return String(d);
          }
        })
        .filter(Boolean);
      if (parts.length) return parts.join(' ');
    }
    if (detail && typeof detail === 'object') {
      if (typeof detail.msg === 'string' && detail.msg.trim()) return detail.msg;
      try {
        return JSON.stringify(detail);
      } catch {
        return 'Failed to send message. Please try again later.';
      }
    }
    if (typeof err?.message === 'string' && err.message.trim()) return err.message;
    return 'Failed to send message. Please try again later.';
  };

  const validate = () => {
    if (!email.trim() || !message.trim()) {
      return 'Please fill out all fields.';
    }
    // Basic email format check
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email.trim())) return 'Please enter a valid email address.';
    // Avoid very short or suspicious messages
    if (message.trim().length < 10) return 'Message is too short. Please provide more details.';
    // Simple heuristic to avoid scammy messages
    const lower = message.toLowerCase();
    const banned = ['crypto giveaway', 'investment scheme', 'wire funds', 'lottery', 'win money', 'cash app', 'forex robot'];
    if (banned.some(w => lower.includes(w))) return 'Message content appears to be inappropriate.';
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (website) {
      // Honeypot filled -> likely bot
      setStatus({ type: 'error', text: 'Submission failed. Please try again.' });
      return;
    }
    const err = validate();
    if (err) {
      setStatus({ type: 'error', text: err });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { email, message });
      setStatus({ type: 'success', text: 'Thanks! Your message has been sent.' });
      setEmail(''); setMessage('');
    } catch (e) {
      setStatus({ type: 'error', text: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto w-full px-6">
      <h2 className="text-2xl font-bold">Contact Us</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        {status && (
          <div className={`p-3 rounded border text-sm ${status.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            {status.text}
          </div>
        )}
        {/* Honeypot field */}
        <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" autoComplete="off" aria-hidden="true" />
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded-md bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border rounded-md bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            placeholder="How can we help?"
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Sending…' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

export default Contact;
